import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { ProspectResult, ProspectSearchQuery } from '@orion/contracts';
import { Avatar, Button, Card, Checkbox, EmptyState, Pill } from '@orion/ui';
import { PageHeader } from '../../app/shell.js';
import { ApiError } from '../../lib/api-client.js';
import { useCreateCompany } from '../companies/api.js';
import { useProspectSearchQuery } from './api.js';
import { LocationAutocomplete } from './LocationAutocomplete.js';
import { ProspectMap } from './ProspectMap.js';

const RADIUS_OPTIONS = [
  { km: 2, label: '2 km' },
  { km: 5, label: '5 km' },
  { km: 10, label: '10 km' },
  { km: 25, label: '25 km' },
];

type OptionalColumn = 'phone' | 'rating' | 'website' | 'onlinePresence';
const COLUMN_LABEL: Record<OptionalColumn, string> = { phone: 'Phone', rating: 'GBP rating', website: 'GBP website', onlinePresence: 'Online presence' };
const ALL_COLUMNS: OptionalColumn[] = ['phone', 'rating', 'website', 'onlinePresence'];

function ManageColumnsMenu({ visible, onToggle }: { visible: Set<OptionalColumn>; onToggle: (c: OptionalColumn) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <Button size="sm" variant="secondary" onClick={() => setOpen((o) => !o)}>
        Manage columns
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div role="menu" className="absolute right-0 top-full z-50 mt-1 w-52 overflow-hidden rounded-dialog border border-border bg-surface py-1.5 shadow-dialog">
            {ALL_COLUMNS.map((c) => (
              <label key={c} className="flex items-center gap-2 px-3.5 py-1.5 text-sm text-ink hover:bg-surface-subtle">
                <input type="checkbox" checked={visible.has(c)} onChange={() => onToggle(c)} />
                {COLUMN_LABEL[c]}
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ViewToggle({ view, onChange }: { view: 'list' | 'grid'; onChange: (v: 'list' | 'grid') => void }) {
  const btnClass = (active: boolean) => `flex h-6.5 w-7.5 items-center justify-center rounded-sm ${active ? 'bg-primary-soft text-primary-text' : 'text-text-faint hover:text-text-muted'}`;
  return (
    <div className="flex items-center gap-0.5 rounded-control border border-border bg-surface p-0.5" role="group" aria-label="Results view">
      <button type="button" aria-label="List view" aria-pressed={view === 'list'} className={btnClass(view === 'list')} onClick={() => onChange('list')}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      <button type="button" aria-label="Grid view" aria-pressed={view === 'grid'} className={btnClass(view === 'grid')} onClick={() => onChange('grid')}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="3" width="7" height="7" rx="1.3" />
          <rect x="14" y="3" width="7" height="7" rx="1.3" />
          <rect x="14" y="14" width="7" height="7" rx="1.3" />
          <rect x="3" y="14" width="7" height="7" rx="1.3" />
        </svg>
      </button>
    </div>
  );
}

const SOCIAL_ICON: Record<string, string> = { facebook: 'f', instagram: 'IG', linkedin: 'in', x: 'X' };

function OnlinePresenceCell({ p }: { p: ProspectResult }) {
  if (!p.website) return <span className="text-text-muted">—</span>;
  if (!p.onlinePresence) return <span className="text-xs text-text-faint">Checking…</span>;
  const { isWordPress, socialLinks } = p.onlinePresence;
  if (!isWordPress && socialLinks.length === 0) return <span className="text-text-muted">—</span>;
  return (
    <div className="flex items-center gap-1">
      {isWordPress && (
        <span title="WordPress" className="flex size-5 items-center justify-center rounded-full bg-[#21759b] text-[9px] font-bold text-white">
          W
        </span>
      )}
      {socialLinks.map((s) => (
        <span key={s} title={s} className="flex size-5 items-center justify-center rounded-full bg-primary-soft text-[8px] font-bold text-primary">
          {SOCIAL_ICON[s]}
        </span>
      ))}
    </div>
  );
}

function RatingPill({ p }: { p: ProspectResult }) {
  if (p.rating == null) return null;
  return <Pill label={`${p.rating.toFixed(1)} ★ (${p.reviewCount ?? 0})`} color="#2439B3" />;
}

function StatusPill({ already }: { already: boolean }) {
  return already ? <Pill label="Added" color="#1E7A4C" /> : <Pill label="Not added" color="#5E6470" />;
}

/** Admin/CRM "Prospecting" — find real local businesses via Google Maps
 * Platform (Places + Geocoding) and turn any of them into a Company with
 * one click. Shows exactly what Google reports (name, address, phone,
 * website, rating, review count) plus one derived-but-disclosed signal —
 * "Online presence" (WordPress/social links, detected from the business's
 * own website, never a Google-reported field) — no invented "likely to
 * convert" score, employee count, or key-people list, since that would
 * mean paying for a company-enrichment lookup (see the Company detail
 * page's "Enrich" action) on every search result whether or not it's ever
 * added to the CRM. */
const isDesignResultsPreview = import.meta.env.DEV && new URLSearchParams(window.location.search).get('__design') === 'ProspectingResults';

export function ProspectingPage() {
  const [keyword, setKeyword] = useState(isDesignResultsPreview ? 'campbell wines' : '');
  const [location, setLocation] = useState(isDesignResultsPreview ? 'Campbell, CA, US' : '');
  const [radiusKm, setRadiusKm] = useState(5);
  const [query, setQuery] = useState<ProspectSearchQuery | null>(
    isDesignResultsPreview ? { keyword: 'campbell wines', location: 'Campbell, CA, US', radiusKm: 5 } : null,
  );
  const [view, setView] = useState<'list' | 'grid'>('list');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  const [addError, setAddError] = useState<string | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<Set<OptionalColumn>>(new Set(ALL_COLUMNS));

  const { data, isFetching, isError, error } = useProspectSearchQuery(query);
  const createCompany = useCreateCompany();

  const results = data ?? [];

  const toggleColumn = (c: OptionalColumn) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  };

  const runSearch = () => {
    if (!keyword.trim() || !location.trim()) return;
    setSelected(new Set());
    setAddError(null);
    setQuery({ keyword: keyword.trim(), location: location.trim(), radiusKm });
  };

  const toggle = (placeId: string) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(placeId)) next.delete(placeId);
      else next.add(placeId);
      return next;
    });
  };

  const toggleAll = () => {
    const addable = results.filter((r) => !r.alreadyAdded && !addedIds.has(r.placeId));
    setSelected((s) => (s.size === addable.length ? new Set() : new Set(addable.map((r) => r.placeId))));
  };

  const addOne = async (p: ProspectResult) => {
    setAddError(null);
    try {
      await createCompany.mutateAsync({
        name: p.name,
        phone: p.phone ?? undefined,
        website: p.website ?? undefined,
        address: p.address,
        lifecycleStage: 'prospect',
        customFields: {},
      });
      setAddedIds((s) => new Set(s).add(p.placeId));
    } catch (err) {
      setAddError(err instanceof ApiError ? err.problem.title : `Could not add "${p.name}".`);
    }
  };

  const addSelected = async () => {
    setAddError(null);
    const toAdd = results.filter((r) => selected.has(r.placeId) && !r.alreadyAdded && !addedIds.has(r.placeId));
    // Sequential, not Promise.all — keeps error attribution to one
    // business clear instead of a jumbled batch failure.
    for (const p of toAdd) {
      await addOne(p);
    }
    setSelected(new Set());
  };

  return (
    <>
      <PageHeader title="Prospecting" />
      <main className="flex flex-1 flex-col gap-4 p-7">
        <Card className="p-5">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h2 className="mb-1 text-base font-semibold text-ink">Find local leads via Google</h2>
              <p className="text-sm text-text-muted">Search real businesses by keyword and location, then add any of them straight into the CRM as a company.</p>
            </div>
            <Link to="/companies/new" className="shrink-0 text-sm font-semibold text-primary hover:underline">
              Add virtual business manually
            </Link>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex min-w-55 flex-1 flex-col gap-1.5 text-[13px] font-semibold text-ink">
              Search keyword / business name
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="e.g. wine bar, dentist, Campbell Wines"
                className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal"
              />
            </label>
            <div className="flex min-w-55 flex-1 flex-col gap-1.5 text-[13px] font-semibold text-ink">
              Location
              <LocationAutocomplete value={location} onChange={setLocation} />
            </div>
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
              Radius
              <select
                value={radiusKm}
                onChange={(e) => setRadiusKm(Number(e.target.value))}
                className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal"
              >
                {RADIUS_OPTIONS.map((r) => (
                  <option key={r.km} value={r.km}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
            <Button size="md" onClick={runSearch} loading={isFetching} disabled={!keyword.trim() || !location.trim()}>
              Search
            </Button>
          </div>
        </Card>

        {addError && (
          <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
            {addError}
          </div>
        )}

        {isError && (
          <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
            {error instanceof ApiError ? error.problem.title : 'Search failed. Please try again.'}
          </div>
        )}

        {query && !isFetching && !isError && results.length === 0 && (
          <EmptyState title="No businesses found" description="Try a broader keyword, a different location, or a larger radius." />
        )}

        {results.length > 0 && <ProspectMap results={results} />}

        {results.length > 0 && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-muted">
                <strong className="text-ink">{results.length}</strong> business{results.length === 1 ? '' : 'es'} found{location ? ` near ${location}` : ''}
              </span>
              <div className="flex items-center gap-2">
                <ViewToggle view={view} onChange={setView} />
                <ManageColumnsMenu visible={visibleColumns} onToggle={toggleColumn} />
                <Button size="sm" variant="secondary" onClick={addSelected} disabled={selected.size === 0} loading={createCompany.isPending}>
                  Add {selected.size || ''} prospect{selected.size === 1 ? '' : 's'}
                </Button>
              </div>
            </div>

            {view === 'list' ? (
              <Card className="overflow-hidden p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-surface-subtle text-xs font-semibold text-text-muted">
                        <th className="w-10 px-4 py-2.5">
                          <Checkbox
                            label="Select all"
                            hideLabel
                            checked={selected.size > 0 && selected.size === results.filter((r) => !r.alreadyAdded).length}
                            onChange={toggleAll}
                          />
                        </th>
                        <th className="px-3 py-2.5 text-left">Business</th>
                        {visibleColumns.has('phone') && <th className="px-3 py-2.5 text-left">Phone</th>}
                        {visibleColumns.has('onlinePresence') && <th className="px-3 py-2.5 text-left">Online presence</th>}
                        {visibleColumns.has('rating') && <th className="px-3 py-2.5 text-left">GBP rating</th>}
                        {visibleColumns.has('website') && <th className="px-3 py-2.5 text-left">GBP website</th>}
                        <th className="px-3 py-2.5 text-left">Status</th>
                        <th className="px-4 py-2.5 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((p) => {
                        const already = p.alreadyAdded || addedIds.has(p.placeId);
                        return (
                          <tr key={p.placeId} className="border-b border-divider last:border-b-0 hover:bg-surface-subtle">
                            <td className="px-4 py-3">
                              <Checkbox label={`Select ${p.name}`} hideLabel checked={selected.has(p.placeId)} onChange={() => toggle(p.placeId)} disabled={already} />
                            </td>
                            <td className="px-3 py-3">
                              <div className="flex items-center gap-2.5">
                                <Avatar name={p.name} size="md" />
                                <div className="min-w-0">
                                  <div className="truncate font-semibold text-ink">{p.name}</div>
                                  {p.address.formatted && <div className="truncate text-xs text-text-faint">{p.address.formatted}</div>}
                                </div>
                              </div>
                            </td>
                            {visibleColumns.has('phone') && <td className="px-3 py-3 text-text-secondary">{p.phone ?? '—'}</td>}
                            {visibleColumns.has('onlinePresence') && (
                              <td className="px-3 py-3">
                                <OnlinePresenceCell p={p} />
                              </td>
                            )}
                            {visibleColumns.has('rating') && <td className="px-3 py-3">{p.rating ? <RatingPill p={p} /> : <span className="text-text-muted">—</span>}</td>}
                            {visibleColumns.has('website') && <td className="px-3 py-3 text-text-secondary">{p.website ? 'Yes' : 'No'}</td>}
                            <td className="px-3 py-3">
                              <StatusPill already={already} />
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                type="button"
                                className="text-xs font-semibold text-primary disabled:cursor-not-allowed disabled:text-text-muted"
                                disabled={already || createCompany.isPending}
                                onClick={() => addOne(p)}
                              >
                                + Add
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            ) : (
              <div className="grid grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-3.5">
                {results.map((p) => {
                  const already = p.alreadyAdded || addedIds.has(p.placeId);
                  return (
                    <Card key={p.placeId} className="flex flex-col gap-3 p-4">
                      <div className="flex items-start gap-2.5">
                        <Avatar name={p.name} size="lg" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[14.5px] font-semibold text-ink">{p.name}</div>
                          {p.address.formatted && <div className="truncate text-xs text-text-faint">{p.address.formatted}</div>}
                        </div>
                        <Checkbox label={`Select ${p.name}`} hideLabel checked={selected.has(p.placeId)} onChange={() => toggle(p.placeId)} disabled={already} className="mt-0.5" />
                      </div>

                      {(visibleColumns.has('rating') && p.rating != null) || (visibleColumns.has('website') && p.website) ? (
                        <div className="flex flex-wrap gap-1.5">
                          {visibleColumns.has('rating') && <RatingPill p={p} />}
                          {visibleColumns.has('website') && p.website && <Pill label="Has website" color="#0F6B63" />}
                        </div>
                      ) : null}

                      {visibleColumns.has('onlinePresence') && (
                        <div className="flex items-center gap-1">
                          <OnlinePresenceCell p={p} />
                        </div>
                      )}

                      <div className="flex items-center justify-between border-t border-divider pt-3">
                        <span className="text-xs text-text-muted">{visibleColumns.has('phone') ? (p.phone ?? '—') : ''}</span>
                        {already ? (
                          <StatusPill already />
                        ) : (
                          <Button size="sm" variant="secondary" disabled={createCompany.isPending} onClick={() => addOne(p)}>
                            + Add
                          </Button>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>
    </>
  );
}
